import React, { useEffect, lazy, Suspense } from "react";
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { useScrollToTop } from "../hooks/useScrollToTop";
import { useUser } from "../contexts/UserContext";
import GameSelection from "./GameSelection";
import { GAMES } from "../config/games";
import { Spinner } from "react-bootstrap";

// Lazy load all games to avoid loading them all at once
const Doctor = lazy(() => import("./games/Doctor"));
const Handyman = lazy(() => import("./games/Handyman"));
const Astronaut = lazy(() => import("./games/Astronaut"));
const BeautyShop = lazy(() => import("./games/BeautyShop"));
const GreetingCardStore = lazy(() => import("./games/GreetingCardStore"));
const GroceryStorePage = lazy(() => import("./games/GroceryStore"));
const JrChef = lazy(() => import("./games/JrChef"));
const GameDev = lazy(() => import("./games/GameDev"));
const SuperHero = lazy(() => import("./games/SuperHero"));
const RideShare = lazy(() => import("./games/RideShare"));
const DeliveryDriver = lazy(() => import("./games/DeliveryDriver"));
const Airline = lazy(() => import("./games/Airline/Airline"));
const MovieTheater = lazy(() => import("./games/MovieTheater/MovieTheater"));
const Library = lazy(() => import("./games/Library/Library"));
const WeatherReporter = lazy(() => import("./games/WeatherReporter"));
const NiceList = lazy(() => import("./games/NiceList"));
const DialUp = lazy(() => import("./games/DialUp"));
const SecretAgency = lazy(() => import("./games/SecretAgency"));
const Classroom = lazy(() => import("./games/Classroom"));
const CatholicMass = lazy(() => import("./games/CatholicMass"));
const Hotel = lazy(() => import("./games/Hotel"));
const Restaurant = lazy(() => import("./games/Restaurant"));

// Loading fallback component
const GameLoadingFallback = () => (
  <div
    className="d-flex flex-column justify-content-center align-items-center"
    style={{
      height: "100vh",
      background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
    }}
  >
    <Spinner animation="border" role="status" style={{ color: "#ff69b4" }}>
      <span className="visually-hidden">Loading game...</span>
    </Spinner>
    <p className="mt-3" style={{ color: "#ffffff", opacity: 0.8 }}>
      Loading game...
    </p>
  </div>
);

// Protected Game Component that checks subscription for premium games
const ProtectedGame = ({ gameKey, children }) => {
  const { subscriptionStatus } = useUser();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const game = GAMES.find((g) => g.path === `/games/${gameKey}`);

    // If game is premium and user doesn't have subscription, redirect
    if (game && !game.isFree && subscriptionStatus?.status !== "active") {
      navigate("/games", {
        state: {
          showSubscriptionFor: game.name,
          from: location.pathname,
        },
      });
    }
  }, [gameKey, subscriptionStatus, navigate, location]);

  const game = GAMES.find((g) => g.path === `/games/${gameKey}`);

  // Allow access if game is free or user has subscription
  if (!game || game.isFree || subscriptionStatus?.status === "active") {
    return <Suspense fallback={<GameLoadingFallback />}>{children}</Suspense>;
  }

  // Show loading or redirect for premium games without subscription
  return null;
};

function Game() {
  useScrollToTop();
  const isDecember = new Date().getMonth() === 11;
  const showNiceList = GAMES.find(
    (g) => g.name === "Nice List" && (!g.showInDecemberOnly || isDecember)
  );

  return (
    <Routes>
      {/* Default route to GameSelection when visiting /game */}
      <Route index element={<GameSelection />} />

      {/* Routes for different games with subscription protection */}
      <Route
        path="airline"
        element={
          <ProtectedGame gameKey="airline">
            <Airline />
          </ProtectedGame>
        }
      />
      <Route
        path="doctor"
        element={
          <ProtectedGame gameKey="doctor">
            <Doctor />
          </ProtectedGame>
        }
      />
      <Route
        path="handyman"
        element={
          <ProtectedGame gameKey="handyman">
            <Handyman />
          </ProtectedGame>
        }
      />
      <Route
        path="astronaut"
        element={
          <ProtectedGame gameKey="astronaut">
            <Astronaut />
          </ProtectedGame>
        }
      />
      <Route
        path="beautyshop"
        element={
          <ProtectedGame gameKey="beautyshop">
            <BeautyShop />
          </ProtectedGame>
        }
      />
      <Route
        path="catholicmass"
        element={
          <ProtectedGame gameKey="catholicmass">
            <CatholicMass />
          </ProtectedGame>
        }
      />
      <Route
        path="greetingcardstore"
        element={
          <ProtectedGame gameKey="greetingcardstore">
            <GreetingCardStore />
          </ProtectedGame>
        }
      />
      <Route
        path="grocerystore"
        element={
          <ProtectedGame gameKey="grocerystore">
            <GroceryStorePage />
          </ProtectedGame>
        }
      />
      <Route
        path="chef"
        element={
          <ProtectedGame gameKey="chef">
            <JrChef />
          </ProtectedGame>
        }
      />
      <Route
        path="classroom"
        element={
          <ProtectedGame gameKey="classroom">
            <Classroom />
          </ProtectedGame>
        }
      />
      <Route
        path="gamedev"
        element={
          <ProtectedGame gameKey="gamedev">
            <GameDev />
          </ProtectedGame>
        }
      />
      <Route
        path="hotel"
        element={
          <ProtectedGame gameKey="hotel">
            <Hotel />
          </ProtectedGame>
        }
      />
      <Route
        path="library"
        element={
          <ProtectedGame gameKey="library">
            <Library />
          </ProtectedGame>
        }
      />
      <Route
        path="movietheater"
        element={
          <ProtectedGame gameKey="movietheater">
            <MovieTheater />
          </ProtectedGame>
        }
      />
      <Route
        path="restaurant"
        element={
          <ProtectedGame gameKey="restaurant">
            <Restaurant />
          </ProtectedGame>
        }
      />
      <Route
        path="superhero"
        element={
          <ProtectedGame gameKey="superhero">
            <SuperHero />
          </ProtectedGame>
        }
      />
      <Route
        path="rideshare"
        element={
          <ProtectedGame gameKey="rideshare">
            <RideShare />
          </ProtectedGame>
        }
      />
      <Route
        path="secretagency"
        element={
          <ProtectedGame gameKey="secretagency">
            <SecretAgency />
          </ProtectedGame>
        }
      />
      <Route
        path="delivery"
        element={
          <ProtectedGame gameKey="delivery">
            <DeliveryDriver />
          </ProtectedGame>
        }
      />
      <Route
        path="dialup"
        element={
          <ProtectedGame gameKey="dialup">
            <DialUp />
          </ProtectedGame>
        }
      />
      <Route
        path="weather"
        element={
          <ProtectedGame gameKey="weather">
            <WeatherReporter />
          </ProtectedGame>
        }
      />
      {/* Only allow access to Nice List during December */}
      {showNiceList && (
        <Route
          path="nicelist"
          element={
            <ProtectedGame gameKey="nicelist">
              <NiceList />
            </ProtectedGame>
          }
        />
      )}

      {/* Redirect unknown paths back to selection screen */}
      <Route path="*" element={<Navigate to="/games" />} />
    </Routes>
  );
}

export default Game;
